import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Button, Paper, Step, StepLabel, Stepper, 
  Typography, Alert 
} from '@mui/material';
import { DateTime } from 'luxon';
import { calculateNextScheduledAt } from '../../utils/scheduler';
import { convertLocalTimeToUTC } from '../../utils/timeUtils';
import apiClient from '../../api/client';
import useAuthToken from '../../hooks/useAuthToken';
import { TimeValue } from '../../utils/TimeValue';

import GeneralSettings from '../../components/automation/GeneralSettings';
import AudienceSelection from '../../components/automation/AudienceSelection';
import AudienceBuilder from '../../components/automation/AudienceBuilder';
import EmailDesigner from '../../components/automation/EmailDesigner';
import ReviewAutomation from '../../components/automation/ReviewAutomation';

import { useAutomations } from '../../hooks/useAutomations';

const steps = ['General Settings', 'Choose Audience', 'Design Email', 'Review & Save'];

const CreateAutomation = () => {
  // Use refs to track updates and prevent loops
  const isUpdatingRef = useRef(false);
  const stepsCompletedRef = useRef({});
  const emailDesignerRef = useRef(null);
  const token = useAuthToken();
  
  const [activeStep, setActiveStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [automationData, setAutomationData] = useState({
    title: '',
    emailSubject: '',
    emailFromId: '',
    scheduleType: 'DAILY',
    scheduleInterval: 1,
    scheduleTime: new TimeValue('09:00:00', 'LOCAL'),
    scheduleDays: [],
    audience: {
      name: 'Default Filter',
      groups: [
        {
          type: 'ALL',
          conditions: []
        },
        {
          type: 'ANY',
          conditions: []
        }
      ]
    },
    emailTemplate: {
      htmlContent: '',
      textContent: ''
    },
    preventDuplicates: false,
  });
  
  const navigate = useNavigate();
  const { createAutomation } = useAutomations();

  // In Create.js - modify handleUpdateData
  const handleUpdateData = useCallback((stepData) => {
    // Skip if we're already processing an update
    if (isUpdatingRef.current) return;
    
    isUpdatingRef.current = true;
    console.log("Updating automation data with:", stepData);
    
    setAutomationData(prevData => {
      const newData = { ...prevData };
      
      // Handle top-level fields
      Object.keys(stepData).forEach(key => {
        if (key !== 'audience' && key !== 'emailTemplate') {
          newData[key] = stepData[key];
        }
      });
      
      // Handle audience data specially - preserve structure but update changed fields
      if (stepData.audience) {
        // Log the incoming audience data for debugging
        console.log('💾 Audience data received in handleUpdateData:', stepData.audience);
        
        // IMPORTANT: Always create a fresh audience object to avoid mixing audience types
        // Check if this is a complex filter selection
        if (stepData.audience.complex_filter_slug) {
          console.log('Complex filter selected:', stepData.audience.complex_filter_slug);
          // For complex filters, completely replace the audience object
          newData.audience = { 
            name: stepData.audience.name || stepData.audience.filter_name || 'Complex Filter',
            complex_filter_slug: stepData.audience.complex_filter_slug
          };
          
          // Clear audienceId since we're using a complex filter
          newData.audienceId = undefined;
        } else {
          // For regular user-created audiences
          console.log('Regular audience selected with ID:', stepData.audience.id);
          
          // Store the audienceId separately for API calls
          if (stepData.audience.id) {
            newData.audienceId = stepData.audience.id;
          }
          
          // Replace audience data completely to avoid confusion with complex filters
          newData.audience = {
            id: stepData.audience.id,
            name: stepData.audience.filter_name || stepData.audience.name,
            filter_name: stepData.audience.filter_name || stepData.audience.name,
            // Ensure groups are preserved if they exist
            groups: stepData.audience.groups || [
              { type: 'ALL', conditions: [] },
              { type: 'ANY', conditions: [] }
            ]
          };
          
          // IMPORTANT: Explicitly remove complex_filter_slug to avoid conflicts
          delete newData.audience.complex_filter_slug;
        }
        
        // Debug log after merging audience
        console.log('💾 After audience merge in Create.js:', newData.audience);
        console.log('💾 audienceId set to:', newData.audienceId);
      }
      
      // Handle email template data
      if (stepData.emailTemplate) {
        newData.emailTemplate = {
          ...prevData.emailTemplate,
          ...stepData.emailTemplate,
        };
      }
      
      return newData;
    });
    
    // Reset update flag after a short delay
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, []);

  const handleNext = useCallback(async () => {
    // Mark current step as completed
    stepsCompletedRef.current[activeStep] = true;

    // If we're on the email designer step, ENSURE we export HTML
    if (activeStep === 2) {
      // Get EmailDesigner component reference and export content
      if (emailDesignerRef.current && emailDesignerRef.current.getTemplateData) {
        await new Promise((resolve) => {
          emailDesignerRef.current.getTemplateData((templateData) => {
            handleUpdateData({
              emailTemplate: templateData
            });
            resolve();
          });
        });
      }
    }

    // Navigate to next step
    setActiveStep(prevStep => prevStep + 1);
  }, [activeStep, handleUpdateData]);

  const handleBack = useCallback(() => {
    // If we're on the review step going back to email designer,
    // make sure our template data is properly preserved
    if (activeStep === 3) {
      console.log("Going back from review to email designer, ensuring template data is preserved");
      console.log("Current template data:", automationData.emailTemplate);
      
      // Force a deep clone of the template data to ensure it's preserved
      const preservedTemplate = JSON.parse(JSON.stringify(automationData.emailTemplate));
      
      // Set a timeout to ensure the state update happens after navigation
      setTimeout(() => {
        handleUpdateData({
          emailTemplate: preservedTemplate
        });
      }, 10);
    }
    
    setActiveStep(prevStep => prevStep - 1);
  }, [activeStep, automationData.emailTemplate, handleUpdateData]);

  const handleSave = useCallback(async (activate = false) => {
    console.log('Starting save process with complete data:', automationData);
    setError(null);
    
    try {
      setIsSaving(true);
      
      // Create the email template first if needed
      let templateId = null;
      
      if (automationData.emailTemplate) {
        try {
          const templateName = `Template for ${automationData.title}`;
          
          // Ensure we have valid content
          const htmlContent = automationData.emailTemplate.htmlContent || "<p>Default content</p>";
          const textContent = automationData.emailTemplate.textContent || "Default text content";
          const designJson = automationData.emailTemplate.designJson || JSON.stringify({
            body: {
              rows: [{
                cells: [1],
                columns: [{
                  contents: [{
                    type: "text",
                    values: {
                      html: htmlContent
                    }
                  }]
                }]
              }]
            }
          });

          const templateResponse = await apiClient.post('/templates', {
            name: templateName,
            html_content: htmlContent,
            text_content: textContent,
            design_json: designJson
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          templateId = templateResponse.data.id;
          console.log('Created template with ID:', templateId);
        } catch (templateError) {
          console.error('Error creating template:', templateError);
          throw new Error('Failed to create email template. Please try again.');
        }
      } else {
        throw new Error('Email template is required. Please design your email first.');
      }

      // Handle time conversion with TimeValue
      console.log('🕒 Create.js - handling time conversion:');
      
      // Get the schedule time value - ensure it's a TimeValue
      const timeValue = automationData.scheduleTime instanceof TimeValue
        ? automationData.scheduleTime
        : new TimeValue(automationData.scheduleTime || '09:00:00', 'LOCAL');
      
      console.log(`🕒 Create.js: Sending TimeValue object to useAutomations: ${timeValue.value} (${timeValue.format})`);

      // Calculate next_scheduled_at using scheduler utility
      const timezone = automationData.timezone || localStorage.getItem('timezone') || 'UTC';
      let nextScheduledAt = null;
      
      try {
        // Use the local time value for scheduling calculation
        nextScheduledAt = calculateNextScheduledAt({
          scheduleType: automationData.scheduleType,
          scheduleInterval: automationData.scheduleInterval,
          scheduleTime: timeValue.value,
          scheduleDay: automationData.scheduleDay,
          scheduleDays: automationData.scheduleDays,
          timezone: timezone
        });
        console.log('🕒 Next scheduled UTC time calculated:', nextScheduledAt);
      } catch (error) {
        console.error('Error calculating nextScheduledAt:', error);
      }
      
      // Debug logging for schedule values
      console.log('DEBUG - Schedule values:', {
        scheduleType: automationData.scheduleType,
        scheduleDay: automationData.scheduleDay,
        typeof_scheduleDay: typeof automationData.scheduleDay
      });

      // Clean up schedule_day value - extract number from "day-X" format if needed
      let cleanScheduleDay = undefined;
      if (automationData.scheduleDay !== undefined && automationData.scheduleDay !== null) {
        let dayValue = automationData.scheduleDay;
        if (typeof dayValue === 'string' && dayValue.startsWith('day-')) {
          dayValue = dayValue.replace('day-', '');
        }
        cleanScheduleDay = Number(dayValue);
        console.log('DEBUG - Schedule day conversion:', automationData.scheduleDay, '→', cleanScheduleDay);
      }

      // Prepare payload - send TimeValue object directly to useAutomations
      const payload = {
        title: String(automationData.title || ""),
        email_subject: String(automationData.emailSubject || ""),
        email_sender_id: automationData.emailFromId,
        schedule_type: String(automationData.scheduleType || "DAILY"),
        schedule_interval: Number(automationData.scheduleInterval || 1),
        scheduleTime: timeValue, // ✅ Send TimeValue object directly
        schedule_days: Array.isArray(automationData.scheduleDays) ? automationData.scheduleDays : [],
        template_id: templateId,
        prevent_duplicates: Boolean(automationData.preventDuplicates),
        status: activate ? 'ACTIVE' : 'DRAFT'
      };

      // FORCE schedule_day to be correct - aggressive fix
      if (cleanScheduleDay !== undefined) {
        payload.schedule_day = cleanScheduleDay;
        console.log('FORCE - Added schedule_day to payload:', payload.schedule_day, typeof payload.schedule_day);
      } else {
        console.log('FORCE - No schedule_day added to payload');
      }
      
      // EXTRA SAFETY: Remove any string schedule_day values
      if (typeof payload.schedule_day === 'string') {
        const stringValue = payload.schedule_day;
        if (stringValue.startsWith('day-')) {
          payload.schedule_day = Number(stringValue.replace('day-', ''));
          console.log('EMERGENCY FIX - Converted string schedule_day:', stringValue, '→', payload.schedule_day);
        } else {
          payload.schedule_day = Number(stringValue);
          console.log('EMERGENCY FIX - Converted other string schedule_day:', stringValue, '→', payload.schedule_day);
        }
      }

      // Handle audience data
      console.log('=== AUDIENCE PROCESSING DEBUG ===');
      console.log('automationData.audienceId:', automationData.audienceId);
      console.log('automationData.audience:', automationData.audience);
      console.log('automationData.audience?.complex_filter_slug:', automationData.audience?.complex_filter_slug);
      
      if (automationData.audience?.complex_filter_slug) {
        // For complex filters, use the slug directly
        payload.complex_filter_slug = automationData.audience.complex_filter_slug;
        console.log('Using complex filter slug:', payload.complex_filter_slug);
      } else if (automationData.audienceId) {
        // For existing audiences, use the ID (backend now supports audience_id)
        payload.audience_id = automationData.audienceId;
        console.log('Using existing audience ID:', payload.audience_id);
      } else if (automationData.audience) {
        // For new audience with conditions - convert to backend format
        console.log('Processing new audience with conditions:', automationData.audience);
        console.log('Audience groups:', automationData.audience.groups);
        
        // Convert frontend audience format to backend schema format
        const conditions = [];
        
        // Process groups and convert to flat conditions list with condition_group field
        if (automationData.audience.groups) {
          automationData.audience.groups.forEach((group, groupIndex) => {
            console.log(`Processing group ${groupIndex}:`, group);
            if (group.conditions && group.conditions.length > 0) {
              group.conditions.forEach((condition, condIndex) => {
                console.log(`  Processing condition ${condIndex}:`, condition);
                conditions.push({
                  field_name: `${condition.recordType}.${condition.fieldName}`,
                  operator: condition.operator,
                  value: condition.value,
                  condition_group: group.type // 'ALL' or 'ANY'
                });
              });
            } else {
              console.log(`  Group ${groupIndex} has no conditions:`, group.conditions);
            }
          });
        } else {
          console.log('No groups found in audience data');
        }

        console.log('Converted conditions:', conditions);

        // Check if we have at least one ALL condition (required by backend)
        const allConditions = conditions.filter(c => c.condition_group === 'ALL');
        const hasAllConditions = allConditions.length > 0;
        console.log('ALL conditions found:', allConditions);
        console.log('Has ALL conditions:', hasAllConditions);
        
        if (!hasAllConditions) {
          console.error('❌ No ALL conditions found - this will fail backend validation');
          throw new Error('At least one required (ALL) condition must be defined for the audience. Please go back and add conditions to your audience.');
        }

        const backendAudience = {
          filter_name: automationData.audience.name || automationData.audience.filter_name || 'Default Filter',
          match_all: true, // Just a boolean flag as expected by schema
          conditions: conditions
        };

        payload.audience = backendAudience;
        console.log('✅ Using schema-compliant audience format for backend:', payload.audience);
      } else {
        console.error('❌ No audience data found');
        throw new Error('No audience selected. Please go back and select or create an audience.');
      }

      // Only add next_scheduled_at if it's valid
      if (nextScheduledAt) {
        payload.next_scheduled_at = nextScheduledAt;
      }

      // FINAL SAFETY CHECK before API call
      console.log('=== FINAL PAYLOAD INSPECTION ===');
      console.log('Full payload:', JSON.stringify(payload, null, 2));
      console.log('schedule_day value:', payload.schedule_day);
      console.log('schedule_day type:', typeof payload.schedule_day);
      
      // Last resort fix if somehow it's still a string
      if (payload.schedule_day && typeof payload.schedule_day === 'string') {
        console.log('LAST RESORT - Converting schedule_day from string:', payload.schedule_day);
        if (payload.schedule_day.startsWith('day-')) {
          payload.schedule_day = Number(payload.schedule_day.replace('day-', ''));
        } else {
          payload.schedule_day = Number(payload.schedule_day);
        }
        console.log('LAST RESORT - Final schedule_day:', payload.schedule_day, typeof payload.schedule_day);
      }
      
      console.log('Creating automation with payload:', payload);
      
      try {
        const result = await createAutomation(payload);
        
        if (result.success) {
          navigate('/automations');
        } else {
          setError('Failed to create automation');
        }
      } catch (apiError) {
        console.error('API Error details:', apiError);
        console.error('API Error response:', apiError.response?.data);
        
        // Extract validation error details
        if (apiError.response?.data?.detail) {
          console.error('Validation errors:', apiError.response.data.detail);
          setError(`Validation error: ${JSON.stringify(apiError.response.data.detail)}`);
        } else {
          setError(apiError.message || 'Failed to create automation');
        }
        return;
      }
    } catch (error) {
      console.error('Error in save process:', error);
      setError(error.message || 'Failed to save automation');
    } finally {
      setIsSaving(false);
    }
  }, [automationData, createAutomation, navigate, token]);

  // Render the current step
  const renderStepContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return (
          <GeneralSettings 
            data={automationData} 
            onUpdate={handleUpdateData}
          />
        );
      // In the AudienceSelection.jsx component, update the onSelectAudience call in renderStepContent:
      case 1:
        return (
          <AudienceSelection
            selectedAudience={automationData.audience}
            preselectedAudienceId={automationData.audienceId}
            onSelectAudience={(audience) => {
              console.log('onSelectAudience called with:', audience);
              
              // Handle complex filter selection
              if (audience.type === 'complex_filter' || audience.complex_filter_slug) {
                console.log('Selecting complex filter:', audience.name);
                handleUpdateData({
                  audience: {
                    name: audience.name,
                    filter_name: audience.name,
                    complex_filter_slug: audience.complex_filter_slug,
                    type: 'complex_filter'
                  },
                  audienceId: null // Explicitly clear regular audience ID
                });
              } 
              // Handle regular audience selection
              else if (audience.type === 'regular_audience' || audience.id) {
                console.log('Selecting regular audience:', audience.name);
                handleUpdateData({
                  audience: {
                    id: audience.id,
                    name: audience.filter_name || audience.name,
                    filter_name: audience.filter_name || audience.name,
                    groups: audience.groups || [
                      { type: 'ALL', conditions: [] },
                      { type: 'ANY', conditions: [] }
                    ],
                    type: 'regular_audience'
                    // Explicitly ensure no complex_filter_slug
                  },
                  audienceId: audience.id
                });
              } else {
                console.error('Unknown audience type:', audience);
              }
            }}
          />
        );
      case 2:
        return (
          <EmailDesigner
            ref={emailDesignerRef}
            data={automationData.emailTemplate}
            subject={automationData.emailSubject}
            isNewAutomation={true}
            onUpdate={(emailTemplate) => {
              handleUpdateData({
                emailTemplate: emailTemplate,
                emailSubject: emailTemplate.subject || automationData.emailSubject
              });
            }}
          />
        );
      case 3:
        console.log('Rendering review step with data:', {
          audienceId: automationData.audienceId,
          audience: automationData.audience,
          useComplexFilter: !!automationData.audience?.complex_filter_slug
        });
        return (
          <ReviewAutomation 
            data={automationData} 
            onSave={handleSave}
            isLoading={isSaving}
            showSaveButtons={false}
          />
        );
      default:
        return 'Unknown step';
    }
  }, [activeStep, automationData, handleUpdateData, handleSave, isSaving]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Create New Automation
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box>
          {renderStepContent()}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <>
                  <Button 
                    variant="outlined" 
                    onClick={() => handleSave(false)}
                    sx={{ mr: 1 }}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save as Draft'}
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => handleSave(true)}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save & Activate'}
                  </Button>
                </>
              ) : (
                <Button variant="contained" onClick={() => {
                  // Validation before proceeding
                  if (activeStep === 0) { // General Settings step
                    if (!automationData || !automationData.emailFromId) {
                      setError("Please select a Sender before proceeding.");
                      return; // Stop progression
                    } 
                    else if (!automationData.title) {
                       setError("Please enter an Automation Title before proceeding.");
                       return; // Stop progression
                    }
                  }
                  // Validation for Audience step
                  else if (activeStep === 1) { 
                    const hasRegularAudience = automationData.audienceId;
                    const hasComplexFilter = automationData.audience?.complex_filter_slug;
                    
                    if (!hasRegularAudience && !hasComplexFilter) {
                      setError("Please select exactly one audience (either a regular audience or a system complex filter) before proceeding.");
                      return; // Stop progression
                    }
                    
                    if (hasRegularAudience && hasComplexFilter) {
                      setError("You can only select one audience type. Please choose either a regular audience OR a complex filter, not both.");
                      return; // Stop progression
                    }
                  }
                  // Add validation for other steps here if necessary
                  
                  setError(null); // Clear any previous validation errors
                  console.log("🔵 User explicitly clicked Next button - validation passed (Create)");
                  handleNext(); // Proceed to next step
                }}
                disabled={isSaving} // Keep disabled check if saving
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateAutomation;